import { PaginatedAPIResponse } from "../models/NetworkResponse";
import {
  GetVendorRequestsParams,
  GetVendorsParams,
  Vendor,
} from "../models/Vendors/Vendor";
import { deleteAPI, getAPI, postAPI, putAPI } from "./Networking";

export default class VendorAPI {
  private isMasked = (value?: string) =>
    typeof value === "string" && value.includes("*");

  getVendors = async (
    params: GetVendorsParams,
  ): Promise<PaginatedAPIResponse<Vendor>> => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page_no)
        queryParams.append("page_no", params.page_no.toString());
      if (params.limit) queryParams.append("limit", params.limit.toString());
      if (params.sortBy) queryParams.append("sort_by", params.sortBy);
      if (params.sortType) queryParams.append("sort_type", params.sortType);
      if (params.search_text)
        queryParams.append("search_text", params.search_text);
      if (params.type) queryParams.append("type", params.type.join(", "));
      if (params.status) {
        queryParams.append("status", params.status.toString());
      }

      const response = await getAPI(`/v5/vendors?${queryParams}`);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getVendorDetailsById = async (vendorId: number): Promise<Vendor> => {
    try {
      const response = await getAPI(`/v5/vendors/vendor-id/${vendorId}`);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  updateVendorDetailsById = async (vendor: Vendor) => {
    try {
      const payload: Vendor = {
        ...vendor,
        // For edit/update, don't send masked top-level email.
        ...(this.isMasked(vendor.email) ? { email: undefined } : {}),
        // Send only newly added POCs (without id) to backend.
        poc_list: (vendor.poc_list || []).filter((poc) => !poc.id),
      };

      const response = await putAPI(`/v5/vendors`, payload);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  deleteVendorPocById = async (vendorId: number, pocId: number) => {
    try {
      const response = await deleteAPI(
        `/v5/vendors/vendor-id/${vendorId}/poc-id/${pocId}`,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  deleteVendorById = async (vendorId: number) => {
    try {
      const response = await deleteAPI(`/v5/vendors/vendor-id/${vendorId}`);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  addNewVendor = async (vendor: Vendor): Promise<Vendor> => {
    try {
      const response = await postAPI(`/v5/vendors/`, vendor);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getVendorRequest = async (
    params: GetVendorRequestsParams,
  ): Promise<PaginatedAPIResponse<Vendor>> => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page_no)
        queryParams.append("page_no", params.page_no.toString());
      if (params.limit) queryParams.append("limit", params.limit.toString());
      if (params.sortBy) queryParams.append("sort_by", params.sortBy);
      if (params.sortType)
        queryParams.append("sort_type", params.sortType.toString());
      if (params.search_text)
        queryParams.append("search_text", params.search_text);
      if (params.missing_gst_doc === true)
        queryParams.append("missing_gst_doc", "true");
      if (params.missing_pan_doc === true)
        queryParams.append("missing_pan_doc", "true");
      if (params.missing_bank_details === true)
        queryParams.append("missing_bank_details", "true");
      if (params.missing_product_catalog === true)
        queryParams.append("missing_product_catalog", "true");
      if (params.missing_msme_doc === true)
        queryParams.append("missing_msme_doc", "true");
      if (
        params.status !== undefined &&
        params.status !== null &&
        (params.status === 1 || params.status === 2 || params.status === 3)
      ) {
        queryParams.append("status", params.status.toString());
      }
      if (params.vendor_types && params.vendor_types !== "0") {
        queryParams.append("vendor_types", params.vendor_types);
      }
      if (
        params.vendor_supplier_types &&
        params.vendor_supplier_types !== "0"
      ) {
        queryParams.append(
          "vendor_supplier_types",
          params.vendor_supplier_types,
        );
      }

      const response = await getAPI(
        `/v5/vendors/requests?${queryParams.toString()}`,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  approveFinanceVendorRequest = async (vendorId: number) => {
    try {
      const response = await putAPI(
        `/v5/vendors/requests/finance-approve/${vendorId}`,
        {
          finance_approved: true,
        },
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  rejectFinanceVendorRequest = async (
    vendorId: number,
    rejectionReason?: string,
  ) => {
    try {
      const response = await putAPI(
        `/v5/vendors/requests/finance-reject/${vendorId}`,
        {
          finance_rejected: true,
          rejection_reason: rejectionReason || "",
        },
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  approveSourcingVendorRequest = async (vendorId: number) => {
    try {
      const response = await putAPI(
        `/v5/vendors/requests/sourcing-approve/${vendorId}`,
        {
          sourcing_approved: true,
        },
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  rejectSourcingVendorRequest = async (
    vendorId: number,
    rejectionReason?: string,
  ) => {
    try {
      const response = await putAPI(
        `/v5/vendors/requests/sourcing-reject/${vendorId}`,
        {
          sourcing_rejected: true,
          rejection_reason: rejectionReason || "",
        },
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };
}
